import { Dialog } from 'primereact/dialog'
import React from 'react'
import QRCode from 'react-qr-code'

function LogModal ({ logsData, setLogsData }) {
  if (!logsData || !logsData[0]) return null

  const invoiceData =
    JSON.parse(logsData[0]?.json_request_from_system || '{}')?.invoiceData || {}
  const invoiceLines = invoiceData.invoiceLines || []
  const customer = invoiceData.customer || {}
  const supplier = invoiceData.supplier || {}

  const formatCurrency = amount => {
    return parseFloat(amount || 0).toFixed(2)
  }

  const calculateLineTotal = line => {
    return (line.netPrice - line.priceDiscount) * line.quantity
  }

  return (
    <Dialog
      header={'Invoice Details'}
      visible={!!logsData}
      onHide={() => setLogsData(null)}
      pt={{
        root: { className: 'w-full lg:w-[800px] mx-auto overflow-auto' },
        header: { className: '!p-2 !bg-darkBg !text-white' },
        content: { className: '!p-3' },
        closeButton: {
          className: 'hover:!bg-black/20 !outline-none !shadow-none'
        },
        closeButtonIcon: { className: '!text-white' }
      }}
    >
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col md:flex-row justify-between gap-4 border-b pb-4'>
          <div className='flex-1'>
            <div className='grid grid-cols-2 gap-2'>
              <div>
                <h3 className='text-sm font-semibold'>Invoice Number:</h3>
                <p>{invoiceData.invoiceNumber || 'N/A'}</p>
              </div>
              <div>
                <h3 className='text-sm font-semibold'>Date:</h3>
                <p>{invoiceData.issueDate || 'N/A'}</p>
              </div>
              <div>
                <h3 className='text-sm font-semibold'>Order Number:</h3>
                <p>{logsData[0]?.client_order_id || 'N/A'}</p>
              </div>
              <div>
                <h3 className='text-sm font-semibold'>Status:</h3>
                <p className='capitalize'>
                  {logsData[0]?.reporting_status?.toLowerCase() || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {logsData[0]?.qr_code && (
            <div className='flex flex-col items-center'>
              <QRCode
                size={100}
                value={logsData[0]?.qr_code}
                viewBox='0 0 256 256'
              />
              <p className='text-xs mt-2'>ZATCA QR Code</p>
            </div>
          )}
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 border-b pb-4'>
          <div>
            <h3 className='text-sm font-semibold mb-2'>Supplier:</h3>
            <p>{supplier.sellerName || 'N/A'}</p>
            <p>VAT: {supplier.sellerTRN || 'N/A'}</p>
            <p>
              {supplier.streetName}, {supplier.buildingNumber}
            </p>
            <p>
              {supplier.cityName}, {supplier.postalCode}
            </p>
          </div>
          <div>
            <h3 className='text-sm font-semibold mb-2'>Customer:</h3>
            <p>{customer.customerName || 'N/A'}</p>
            <p>VAT: {customer.vatRegNumber || 'N/A'}</p>
            <p>
              {customer.streetName}, {customer.buildingNumber}
            </p>
            <p>
              {customer.cityName}, {customer.zipCode}
            </p>
          </div>
        </div>

        <div className='overflow-x-auto'>
          <table className='w-full border-collapse'>
            <thead className='bg-gray-100'>
              <tr>
                <th className='border p-2 text-left text-xs font-semibold'>
                  #
                </th>
                <th className='border p-2 text-left text-xs font-semibold'>
                  Description
                </th>
                <th className='border p-2 text-center text-xs font-semibold'>
                  Qty
                </th>
                <th className='border p-2 text-right text-xs font-semibold'>
                  Unit Price
                </th>
                <th className='border p-2 text-right text-xs font-semibold'>
                  Discount
                </th>
                <th className='border p-2 text-right text-xs font-semibold'>
                  Net Price
                </th>
                <th className='border p-2 text-right text-xs font-semibold'>
                  VAT
                </th>
                <th className='border p-2 text-right text-xs font-semibold'>
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {invoiceLines.map((line, index) => (
                <tr key={index}>
                  <td className='border p-2 text-xs'>{index + 1}</td>
                  <td className='border p-2 text-xs'>{line.productName}</td>
                  <td className='border p-2 text-center text-xs'>
                    {line.quantity}
                  </td>
                  <td className='border p-2 text-right text-xs'>
                    {formatCurrency(line.netPrice)}
                  </td>
                  <td className='border p-2 text-right text-xs'>
                    {formatCurrency(line.priceDiscount)}
                  </td>
                  <td className='border p-2 text-right text-xs'>
                    {formatCurrency(line.netPrice - line.priceDiscount)}
                  </td>
                  <td className='border p-2 text-right text-xs'>0%</td>
                  <td className='border p-2 text-right text-xs'>
                    {formatCurrency(calculateLineTotal(line))}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className='bg-gray-50'>
              <tr>
                <td
                  colSpan='6'
                  className='border p-2 text-right font-semibold text-sm'
                >
                  Subtotal:
                </td>
                <td className='border p-2 text-right text-sm'>
                  {formatCurrency(
                    invoiceLines.reduce(
                      (sum, line) => sum + calculateLineTotal(line),
                      0
                    )
                  )}
                </td>
              </tr>
              <tr>
                <td
                  colSpan='6'
                  className='border p-2 text-right font-semibold text-sm'
                >
                  VAT (0%):
                </td>
                <td className='border p-2 text-right text-sm'>0.00</td>
              </tr>
              <tr>
                <td
                  colSpan='6'
                  className='border p-2 text-right font-semibold text-sm'
                >
                  Total:
                </td>
                <td className='border p-2 text-right text-sm'>
                  {formatCurrency(
                    invoiceLines.reduce(
                      (sum, line) => sum + calculateLineTotal(line),
                      0
                    )
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className='text-xs text-gray-600'>
          <p>
            <strong>Invoice Type:</strong>{' '}
            {invoiceData.invoiceTypeCode === 383 ? 'Debit Note' : 'Standard'}
          </p>
          <p>
            <strong>Notes:</strong> {invoiceData.notes || 'None'}
          </p>
          <p>
            <strong>Reported to ZATCA:</strong>{' '}
            {logsData[0]?.is_reported_to_zatca === '1' ? 'Yes' : 'No'}
          </p>
          <p>
            <strong>Invoice Hash:</strong> {logsData[0]?.invoice_hash || 'N/A'}
          </p>
        </div>
      </div>
    </Dialog>
  )
}

export default LogModal
